import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, MapPin, Star, Globe, Images } from 'lucide-react';

const AchievementsBanner = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="px-4 md:px-8 pb-8 max-w-5xl lg:mx-auto"
    >
      <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-amber-950/30 dark:via-yellow-950/20 dark:to-orange-950/20 rounded-2xl border border-amber-200/60 dark:border-amber-800/40 p-6 shadow-lg overflow-hidden relative">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-amber-400/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-24 h-24 bg-orange-400/10 rounded-full translate-x-1/2 translate-y-1/2" />

        {/* Header */}
        <div className="flex items-center justify-center gap-2 mb-5 relative">
          <Trophy className="w-7 h-7 text-amber-500" />
          <h3 className="text-lg font-black text-foreground font-heading">
            🏆 {t('إنجازات الأكاديمية', 'Academy Achievements')} 🏆
          </h3>
          <Trophy className="w-7 h-7 text-amber-500" />
        </div>

        {/* Competition title */}
        <div className="text-center mb-5">
          <span className="inline-block bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md">
            {t('مسابقة Codeavour على مستوى المملكة', 'Codeavour National Competition')}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative">
          {/* Girls Team */}
          <div className="bg-white/70 dark:bg-white/5 rounded-xl p-4 border border-pink-200/50 dark:border-pink-800/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                <Star className="w-4 h-4 text-pink-500" />
              </div>
              <h4 className="text-sm font-bold text-foreground">
                {t('🥉 فوز فريق البنات', '🥉 Girls Team Win')}
              </h4>
            </div>
            <div className="space-y-1.5 text-xs text-muted-foreground">
              <p className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-pink-400 shrink-0" />
                {t('ريا فهد الشعوان', 'Raya Fahd Al-Shaawan')}
              </p>
              <p className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-pink-400 shrink-0" />
                {t('فاطمة عبدالعزيز المعقل', 'Fatimah Abdulaziz Al-Maaqel')}
              </p>
              <p className="mt-2 text-[11px] font-semibold text-foreground/80">
                {t('بقيادة المعلمة: إيثار الخليفة', 'Led by Teacher: Eithar Al-Khalifa')}
              </p>
              <p className="text-[11px] font-bold text-amber-600 dark:text-amber-400">
                {t('المركز الثالث - مسار الابتكار جونيور', '3rd Place - Innovation Junior Track')}
              </p>
            </div>
          </div>

          {/* Boys Team */}
          <div className="bg-white/70 dark:bg-white/5 rounded-xl p-4 border border-blue-200/50 dark:border-blue-800/30">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Star className="w-4 h-4 text-blue-500" />
              </div>
              <h4 className="text-sm font-bold text-foreground">
                {t('🥉 فوز فريق البنين', '🥉 Boys Team Win')}
              </h4>
            </div>
            <div className="space-y-1.5 text-xs text-muted-foreground">
              <p className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-blue-400 shrink-0" />
                {t('محمد ياسر العباس', 'Mohammed Yasser Al-Abbas')}
              </p>
              <p className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-blue-400 shrink-0" />
                {t('عبدالعزيز بدر النصر', 'Abdulaziz Badr Al-Nasr')}
              </p>
              <p className="mt-2 text-[11px] font-semibold text-foreground/80">
                {t('بقيادة المعلم: محمد الشنهابي', 'Led by Teacher: Mohammed Al-Shanhabi')}
              </p>
              <p className="text-[11px] font-bold text-amber-600 dark:text-amber-400">
                {t('المركز الثالث - مسار الابتكار سينيور', '3rd Place - Innovation Senior Track')}
              </p>
            </div>
          </div>
        </div>

        {/* Qualification & Congratulations */}
        <div className="mt-4 text-center space-y-2">
          <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-md">
            <Globe className="w-3.5 h-3.5" />
            {t('تأهل الفريقين للمشاركة على مستوى العالم في إندونيسيا 🇮🇩', 'Both teams qualified for the World Competition in Indonesia 🇮🇩')}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {t(
              'ألف مليون مبروك للأكاديمية وقيادتها وقيادات المراحل والشريك الداعم دائماً: أ. مي بركات',
              'Congratulations to the Academy, its leadership, and supporting partner: Ms. Mai Barakat'
            )}
          </p>
          <p className="text-sm font-black bg-gradient-to-r from-purple-600 via-pink-500 to-amber-500 bg-clip-text text-transparent">
            {t('من SCAi للعالمية بفضل الله ✨', 'From SCAi to the World ✨')}
          </p>
          <button
            onClick={() => navigate('/gallery')}
            className="mt-3 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs px-5 py-2.5 rounded-full transition-colors shadow-md"
          >
            <Images className="w-4 h-4" />
            {t('📷 معرض الصور', '📷 Photo Gallery')}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default AchievementsBanner;
